/**
 * LeftMenuModuleRail — 좌측 사이드바 본문 영역의 narrow 모듈 스위처
 *
 * 워크스페이스 헤더 아래에 좌우 2-column 레이아웃의 좌측 컬럼으로 배치.
 * 채널 / 게시판 / 캘린더 세로 아이콘 버튼. 클릭 시 activeModule 갱신 + 라우팅.
 *
 * '채널' 모듈은 활성 워크스페이스의 마지막 방문 채널로 복원 (LocalStorage).
 *
 * 캘린더 아래에는 GIGAPOD 바로가기가 붙을 수 있다. 이것만은 내부 모듈이 아니라
 * 외부 사이트 링크(설정: workspace_settings.gigapod)라 RAIL_ITEMS 에 넣지 않고
 * 별도 블록으로 렌더한다 — activeModule 을 건드리지 않게 하기 위함.
 */
import React, { useState, useEffect } from 'react';
import { Hash, LayoutList, Calendar } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { t } from '@lingui/macro';
import { useAppUIContext, AppModule } from '../../context/AppUIContext';
import { useHistory } from '../../hooks/useRouterCompat';
import Page from '../../const/Page';
import { cn } from '../../lib/utils';
import { LocalStorage } from '../../services/Utils/LocalStorage';
import { MyNotifs } from '../../services/MyNotifs';
import { useWorkspaceSettings } from '../../context/WorkspaceSettingsContext';
import { shouldShowGigapodLink } from '../../models/WorkspaceSettings';
import { GIGAPOD_ICON_SRC, GIGAPOD_LABEL } from '../../const/Gigapod';

interface RailItem {
  module: AppModule;
  icon: React.ElementType;
  label: string;
  path: string;
}

const LeftMenuModuleRail: React.FC = () => {
  const RAIL_ITEMS: RailItem[] = [
    { module: 'chat',     icon: Hash,       label: t`Channels`, path: '/main' },
    { module: 'board',    icon: LayoutList, label: t`Boards`,   path: Page.board },
    { module: 'calendar', icon: Calendar,   label: t`Calendar`, path: Page.calendar },
  ];
  const { activeModule, setActiveModule, activeWorkspaceId, rooms } = useAppUIContext();
  const history = useHistory();

  // GIGAPOD 바로가기 — 관리자가 체크 ON + 유효한 http(s) URL 을 저장했을 때만 노출
  const { gigapod } = useWorkspaceSettings();
  const showGigapod = shouldShowGigapodLink(gigapod);

  // 웹은 새 탭, 네이티브(iOS/Android)는 인앱 브라우저 — DocumentViewerModal 과 동일 분기
  const handleGigapodClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!Capacitor.isNativePlatform()) return;   // 웹은 <a target="_blank"> 기본 동작에 맡김
    e.preventDefault();
    import('@capacitor/browser').then(({ Browser }) => {
      Browser.open({ url: gigapod.url });
    });
  };

  // 워크스페이스 게시판 미읽음 알림 → Boards 모듈 아이콘에 오렌지 점 (채널 탭바와 동일: 알림 isNew 기준)
  const [boardHasUnread, setBoardHasUnread] = useState(false);
  const [calendarHasUnread, setCalendarHasUnread] = useState(false);
  useEffect(() => {
    const compute = () => {
      setBoardHasUnread(MyNotifs.Notifications.some(n => n.isNew && n.source === 'board' && n.channelId == null));
      // 캘린더 모듈은 전역(CalendarPage)이라 ws·채널 일정이 모두 보이므로 channelId 무관하게 집계
      setCalendarHasUnread(MyNotifs.Notifications.some(n => n.isNew && n.source === 'calendar'));
    };
    compute();
    return MyNotifs.subscribeToNotificationUpdates(compute);
  }, []);

  const handleClick = (item: RailItem) => {
    setActiveModule(item.module);
    // 채널 모듈 → 활성 워크스페이스의 마지막 방문 채널 복원
    if (item.module === 'chat' && activeWorkspaceId) {
      const lastRoomId = LocalStorage.get(`last-visited-room:${activeWorkspaceId}`);
      if (lastRoomId && rooms.some(r => r.id === lastRoomId && r.workspaceId === activeWorkspaceId)) {
        history.push(`/channel/${lastRoomId}`);
        return;
      }
    }
    history.push(item.path);
  };

  return (
    <nav className="flex flex-col items-start w-[68px] shrink-0 py-2 pl-4 gap-1 border-r border-border/60">
      {RAIL_ITEMS.map((item) => {
        const isActive = activeModule === item.module;
        const Icon = item.icon;
        return (
          <button
            key={item.module}
            onClick={() => handleClick(item)}
            className={cn(
              'relative flex flex-col items-center justify-center w-12 h-12 rounded-xl transition-all duration-200 cursor-pointer',
              isActive
                ? 'text-[color:rgb(var(--rail-icon-active))] bg-[color:var(--rail-active-bg)]'
                : 'text-[color:rgb(var(--rail-icon))] hover:text-foreground hover:bg-accent/50',
            )}
            title={item.label}
            aria-label={item.label}
          >
            <Icon className="size-5" strokeWidth={isActive ? 2.2 : 1.8} />
            {item.module === 'board' && boardHasUnread && (
              <span className="absolute top-1.5 right-2 size-1.5 rounded-full bg-primary" aria-label="unread" />
            )}
            {item.module === 'calendar' && calendarHasUnread && (
              <span className="absolute top-1.5 right-2 size-1.5 rounded-full bg-primary" aria-label="unread" />
            )}
            <span className={cn(
              'text-micro leading-tight mt-0.5 font-medium',
              isActive ? 'opacity-100' : 'opacity-70',
            )}>
              {item.label}
            </span>
          </button>
        );
      })}

      {/* GIGAPOD — 외부 사이트 링크. 내부 모듈이 아니라 activeModule 을 바꾸지 않는다. */}
      {showGigapod && (
        <a
          href={gigapod.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleGigapodClick}
          className="relative flex flex-col items-center justify-center w-12 h-12 rounded-xl transition-all duration-200 cursor-pointer text-[color:rgb(var(--rail-icon))] hover:text-foreground hover:bg-accent/50"
          title={t`Open GIGAPOD in a new tab`}
          aria-label={t`Open GIGAPOD in a new tab`}
        >
          <img src={GIGAPOD_ICON_SRC} alt="" aria-hidden="true" className="size-5 object-contain" />
          <span className="text-micro leading-tight mt-0.5 font-medium opacity-70">
            {GIGAPOD_LABEL}
          </span>
        </a>
      )}
    </nav>
  );
};

export default LeftMenuModuleRail;
